import { useEffect, useState } from 'react';
import { useSales } from '../store/salesStore';
import { toast } from 'sonner';

const METHODS = ['bank transfer', 'paypal', 'stripe'];

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function Payouts() {
  const { payouts, stats, isLoading, error, fetchPayouts, fetchStats, requestPayout } = useSales();

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState(METHODS[0]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([fetchPayouts().catch(() => null), fetchStats().catch(() => null)]);
  }, [fetchPayouts, fetchStats]);

  const available =
    (stats?.total_earnings ?? 0) -
    (payouts ?? [])
      .filter((p) => p.status === 'approved' || p.status === 'completed' || p.status === 'pending')
      .reduce((sum, p) => sum + p.amount, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (Number.isNaN(parsed) || parsed <= 0) {
      toast.error('Enter a valid positive amount');
      return;
    }
    if (parsed > available) {
      toast.error(`Requested amount exceeds available balance (${available.toFixed(2)})`);
      return;
    }
    setSubmitting(true);
    try {
      await requestPayout(parsed, method, note.trim() || undefined);
      toast.success('Payout requested');
      setAmount('');
      setNote('');
      await Promise.all([fetchPayouts(), fetchStats()]);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h1 className="page-title">Payouts</h1>
      <p className="page-subtitle">
        Request a withdrawal of your earnings. Available balance:{' '}
        <strong style={{ color: 'var(--success)' }}>€{Math.max(0, available).toFixed(2)}</strong>
      </p>

      {error && <p className="form-error">{error}</p>}

      <div className="panel">
        <h2>Request Payout</h2>
        <form className="form" onSubmit={handleSubmit}>
          <div className="form-row">
            <label htmlFor="amount">Amount (EUR) *</label>
            <input
              id="amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="method">Payout Method</label>
            <select id="method" value={method} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="note">Note (optional)</label>
            <input
              id="note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Payment details, invoice reference…"
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? 'Requesting…' : 'Request Payout'}
            </button>
          </div>
        </form>
      </div>

      <div className="panel">
        <h2>Payout History</h2>
        {isLoading && payouts.length === 0 ? (
          <div className="loading-spinner">
            <div className="spinner" />
          </div>
        ) : payouts.length === 0 ? (
          <p className="empty-state">No payouts yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id}>
                    <td>{formatDate(p.created_at)}</td>
                    <td>€{p.amount.toFixed(2)}</td>
                    <td>{p.method}</td>
                    <td>
                      <span className={`status-badge status--${p.status}`}>{p.status}</span>
                    </td>
                    <td>{p.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Payouts;
